import { getProductCodename } from '@recruitee/billing-plans';
import type { BillingPlan } from '@recruitee/products-types';

import type { SlotLimitExceeded } from '../../../helpers/calculate-slots-limit';
import { calculateSlotsLimitExceeded } from '../../../helpers/calculate-slots-limit';
import { getProductCost } from '../../../helpers/get-product-cost';
import type {
  PlanPickerProduct,
  PlanPickerProductState,
  PlanPickerStateFactoryData,
  RecognizedProductCode,
} from '../plan-picker.types';
import { ProductStatus } from '../plan-picker.types';
import { getPlanPickerProductStatus } from './get-plan-picker-product-status';

const empty = (showCta = true): PlanPickerProductState => ({
  showCta,
  ownedSlots: Number.POSITIVE_INFINITY,
  disableCta: false,
  showCustomBadge: false,
  showSlotLimitMessage: false,
  costPerMonthWithoutDiscount: Number.POSITIVE_INFINITY,
  costPerMonth: Number.POSITIVE_INFINITY,
  selectedSlots: Number.NEGATIVE_INFINITY,
  isActive: false,
  showPrice: false,
  showSlotPicker: false,
  showChurn: false,
});

// eslint-disable-next-line max-lines-per-function
const planPickerProductState = (
  codename: RecognizedProductCode,
  data: PlanPickerStateFactoryData,
  selectedSlots: number,
  product: BillingPlan,
  status: ProductStatus,
  limits: SlotLimitExceeded,
): // eslint-disable-next-line max-params-no-constructor/max-params-no-constructor
PlanPickerProductState => {
  const isActive = data.activeProduct && data.activeProduct.id === product?.id;
  const isTrial = data.isTrial;
  const isExternalBilling = data.isExternalBilling;
  const subscriptionCancellationInProgress = data.subscriptionStatus === 'non_renewing';
  const anySubscriptionActive = data.isSubscriptionActive;
  const currentProductIsActive =
    getProductCodename(data.activeProduct) === getProductCodename(product);

  function getCostPerMonth() {
    return getProductCost(product, selectedSlots, data.interval, data.addons) / data.interval;
  }

  function getCostPerMonthWithoutDiscount() {
    switch (data.interval) {
      case 12:
        return getProductCost(product, selectedSlots, 1, data.addons);

      default:
        return undefined;
    }
  }

  return {
    ownedSlots: data.ownedSlots,
    disableCta:
      status === ProductStatus.Owned ||
      status === ProductStatus.ManualServiceOwned ||
      (limits.exceeded && status >= ProductStatus.Owned),
    showCta: data.assignedProduct ? product && data.assignedProduct.id === product.id : true,
    showCustomBadge: status !== ProductStatus.Unavailable && product?.custom,
    showSlotLimitMessage: limits.exceeded && status !== ProductStatus.Unavailable,
    showChurn:
      !isTrial &&
      !isExternalBilling &&
      !subscriptionCancellationInProgress &&
      anySubscriptionActive &&
      currentProductIsActive,
    selectedSlots,
    isActive,
    costPerMonth: getCostPerMonth(),
    costPerMonthWithoutDiscount: getCostPerMonthWithoutDiscount(),
    showPrice: status >= ProductStatus.Owned,
    showSlotPicker: status >= ProductStatus.Owned || selectedSlots === Number.POSITIVE_INFINITY,
  };
};

export const planPickerProductFactory = (
  codename: RecognizedProductCode,
  data: PlanPickerStateFactoryData,
  selectedSlots: number,
  newProduct?: BillingPlan,
  assignedProduct?: BillingPlan,
): PlanPickerProduct => {
  const { activeProduct } = data;
  const isProductEqual =
    newProduct?.tier === activeProduct?.tier && newProduct?.currency === activeProduct?.currency;
  const product = isProductEqual ? { ...activeProduct, id: newProduct?.id } : newProduct;
  const status = getPlanPickerProductStatus(data, selectedSlots, product, assignedProduct);
  const limits =
    product && data.isSelfService && !(<BillingPlan>product).unlimited
      ? calculateSlotsLimitExceeded(selectedSlots, data.jobSlotsUsed)
      : { exceeded: false, jobs: 0 };

  return {
    codename,
    product,
    status,
    limits,
    state: product
      ? planPickerProductState(codename, data, selectedSlots, product, status, limits)
      : empty(!data.assignedProduct),
  };
};
